import { NotifyReact, SetPhraseEligible, SetPhrasesTillNextTier } from 'Phaser/EventBus';
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import { PlayablePhrase } from 'Phaser/Scenes/SightReadingPhaser';
import Logger from 'Utils/Logger';

enum ChevronState {
  Eligible,
  NextEligible,
  Dormant,
  Losing,
}

interface CallbackFunction {
  callback: (nextPhrase: PlayablePhrase, activePhrase: PlayablePhrase) => void;
  context?: object;
}

export default class ChevronManager {
  private phrasesTilNextTier = 4;
  private chevronState: ChevronState = ChevronState.Dormant;
  private eventStream: EventStreamPhaser2 | OSMDEventStream;
  private firstPhrasePlayed = false;
  private onGainChevron?: CallbackFunction;
  private onLoseChevron?: CallbackFunction;

  // Customisable phrases till next tier, for use with speeding up/slowing down in repertoire.
  constructor(
    eventStream: EventStreamPhaser2 | OSMDEventStream,
    phrasesTillNextTier: number = 4,
    onGainChevron?: CallbackFunction,
    onLoseChevron?: CallbackFunction,
  ) {
    this.eventStream = eventStream;
    this.phrasesTilNextTier = phrasesTillNextTier;
    this.onGainChevron = onGainChevron;
    this.onLoseChevron = onLoseChevron;
  }

  public reset(): void {
    this.phrasesTilNextTier = 4;
    this.chevronState = ChevronState.Dormant;
    this.firstPhrasePlayed = false;
    Logger.debug('[ChevronManager] reset to initial state');
  }

  public onPhraseFinishPlaying(nextPhrase?: PlayablePhrase, activePhrase?: PlayablePhrase) {
    Logger.debug(
      `[ChevronManager] finishedPlayingPhrase\n
        Chevron State: ${this.chevronState}`,
    );
    this.firstPhrasePlayed = true;
    switch (this.chevronState) {
      case ChevronState.Dormant: {
        const acc = this.eventStream.calcAccuracy();
        if (acc >= 95) {
          this.chevronState = ChevronState.Eligible;
          NotifyReact(SetPhraseEligible(true));
        }
        break;
      }
      case ChevronState.Eligible:
        this.gainChevron(nextPhrase, activePhrase);
        break;
      case ChevronState.Losing:
        this.loseChevron(nextPhrase, activePhrase);
        break;
      case ChevronState.NextEligible:
        this.chevronState = ChevronState.Eligible;
        break;
    }
  }

  private gainChevron(nextPhrase?: PlayablePhrase, activePhrase?: PlayablePhrase) {
    this.phrasesTilNextTier = Math.max(this.phrasesTilNextTier - 1, 0);
    Logger.debug(
      `[ChevronManager] gained chevron\n
        Phrases till next tier: ${this.phrasesTilNextTier}
        ${nextPhrase}
        ${this.onGainChevron}`,
    );
    NotifyReact(SetPhrasesTillNextTier(this.phrasesTilNextTier));
    if (nextPhrase && activePhrase)
      this.onGainChevron?.callback.call(this.onGainChevron?.context, nextPhrase, activePhrase);
  }

  private loseChevron(nextPhrase?: PlayablePhrase, activePhrase?: PlayablePhrase) {
    this.phrasesTilNextTier = Math.min(this.phrasesTilNextTier + 1, 4);
    Logger.debug(
      `[ChevronManager] lost chevron\n
        Phrases till next tier: ${this.phrasesTilNextTier}`,
    );
    NotifyReact(SetPhrasesTillNextTier(this.phrasesTilNextTier));
    if (nextPhrase && activePhrase)
      this.onLoseChevron?.callback.call(this.onLoseChevron?.context, nextPhrase, activePhrase);
  }

  public onTick() {
    Logger.debug(
      `[ChevronManager] onTick\n
        firstPhrasePlayed: ${this.firstPhrasePlayed}`,
    );
    if (!this.firstPhrasePlayed) return;
    const acc = this.eventStream.calcAccuracy();
    if (acc === undefined) {
      Logger.warn('[ChevronManager] Accuracy undefined!');
      return;
    }
    if (acc < 75 && this.chevronState != ChevronState.Losing) {
      this.chevronState = ChevronState.Losing;
      NotifyReact(SetPhraseEligible(false));
    } else if (acc < 95 && this.chevronState != ChevronState.Dormant) {
      this.chevronState = ChevronState.Dormant;
      NotifyReact(SetPhraseEligible(false));
    } else if (
      this.chevronState != ChevronState.Eligible &&
      this.chevronState != ChevronState.NextEligible
    ) {
      NotifyReact(SetPhraseEligible(true));
      this.chevronState = ChevronState.NextEligible;
    }
  }
}
